import { Fuel } from "@/types/fuels/fuels.types";
import { Button } from "@/components/ui/button";
import { Pencil, Archive, ArchiveRestore } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Column } from "@/components/ui/data-table/types";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

interface UseFuelColumnsProps {
  t: (key: string, params?: Record<string, string>) => string;
  onEdit: (fuel: Fuel) => void;
  onArchiveAction: (action: { type: "archive" | "restore"; fuelId: string; fuelName: string }) => void;
}

export const useFuelColumns = ({ t, onEdit, onArchiveAction }: UseFuelColumnsProps): Column<Fuel>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  // Helper functions to extract sortable values
  const getEmissionFactor = (fuel: Fuel): number => {
    const unit = fuel.units[0];
    if (unit === "GJ") return fuel.carbon_factors.fe_comb_GJ ?? -1;
    if (unit === "L") return fuel.carbon_factors.fe_comb_L ?? -1;
    return -1;
  };

  const getHydricFactor = (fuel: Fuel): number => {
    const unit = fuel.units[0];
    if (unit === "GJ") return fuel.hydric_factors.fh_comb_GJ ?? -1;
    if (unit === "L") return fuel.hydric_factors.fh_comb_L ?? -1;
    return -1;
  };

  const getDensity = (fuel: Fuel): number => {
    const unit = fuel.units[0];
    if (unit === "L") return fuel.densities.dens_comb ?? -1;
    return -1;
  };

  const baseColumns: Column<Fuel>[] = [
    {
      header: t("fuels.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: Fuel) => <div>{row.name?.toString() || ""}</div>,
    },
    {
      header: t("fuels.table.units"),
      accessorKey: "units",
      sortable: true,
      className: "min-w-[80px]",
      cell: (_: string, row: Fuel) => <div>{(row.units || [])[0] || ""}</div>,
    },
    {
      header: `${t("fuels.table.fe_comb")} (kg CO₂eq/GJ ou L)`,
      accessorKey: "name", // Using name as a placeholder since we'll override sorting
      sortable: true,
      className: "min-w-[180px]",
      cell: (_: string, row: Fuel) => {
        const value = getEmissionFactor(row);
        return <div>{value >= 0 ? formatNumberDynamic(value) : "-"}</div>;
      },
    },
    {
      header: `${t("fuels.table.fh_comb")} (L/GJ ou L)`,
      accessorKey: "name", // Using name as a placeholder since we'll override sorting
      sortable: true,
      className: "min-w-[160px]",
      cell: (_: string, row: Fuel) => {
        const value = getHydricFactor(row);
        return <div>{value >= 0 ? formatNumberDynamic(value) : "-"}</div>;
      },
    },
    {
      header: `${t("fuels.table.density")} (kg/m³)`,
      accessorKey: "name", // Using name as a placeholder since we'll override sorting
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: Fuel) => {
        const value = getDensity(row);
        return <div>{value >= 0 ? formatNumberDynamic(value) : "-"}</div>;
      },
    },
  ];

  const actionsColumn: Column<Fuel> = {
    header: t("fuels.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_: string, row: Fuel) => (
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(row)}
                className="h-8 w-8"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("fuels.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  onArchiveAction({
                    type: row.isArchived ? "restore" : "archive",
                    fuelId: row.id,
                    fuelName: row.name,
                  })
                }
                className="h-8 w-8"
              >
                {row.isArchived ? (
                  <ArchiveRestore className="h-4 w-4" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{row.isArchived ? t("fuels.actions.restore") : t("fuels.actions.archive")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
}; 